import { Button } from "@/components/ui/button";
import {
  Home,
  PiggyBank,
  HandHeart,
  TrendingUp,
  Users,
  MapPin,
  CheckCircle,
  ArrowRight,
} from "lucide-react";
import janineBorisHardi from "@/assets/janine-boris-hardi.jpg";
import janineBorisHardiWebP from "@/assets/janine-boris-hardi.webp";

const rentingOptions = [
  {
    id: "leibrente",
    title: "Leibrente",
    icon: PiggyBank,
    description: "Monatliche Rente bis zum Lebensende bei Verbleib in der Immobilie",
    features: ["Lebenslange Zahlung", "Wohnrecht bleibt", "Keine Erbschaftssteuer"],
    idealFor: "Senioren ab 75 Jahren, die Sicherheit schätzen"
  },
  {
    id: "teilverkauf",
    title: "Teilverkauf",
    icon: TrendingUp,
    description: "Verkauf eines Teils der Immobilie bei Erhalt des Wohnrechts",
    features: ["Sofortige Auszahlung", "Flexibel wählbarer Anteil", "Erbe bleibt teilweise erhalten"],
    idealFor: "Eigentümer, die Flexibilität und Liquidität benötigen"
  },
  {
    id: 3,
    title: "Rückmiete",
    icon: Home,
    description: "Verkauf der Immobilie mit anschließender Anmietung",
    features: ["Komplette Liquidität", "Planbare Mietkosten", "Keine Instandhaltung"],
    idealFor: "Bei hohem Kapitalbedarf oder Umzugswunsch"
  },
  {
    id: 4,
    title: "Nießbrauch",
    icon: HandHeart,
    description: "Übertragung an Erben mit lebenslangem Wohn- und Nutznießrecht",
    features: ["Schenkung zu Lebzeiten", "Steuervorteile", "Erbschaft geregelt"],
    idealFor: "Familienorientierte Lösung mit Steueroptimierung"
  },
  {
    id: 5,
    title: "Wohnrecht",
    icon: Users,
    description: "Wohnen bleiben mit Grundbuchlich gesichertem Wohnrecht",
    features: ["Einmalzahlung oder Zeitrente", "Sicherheit", "Eigener Herr sein"],
    idealFor: "Eigentümer die mit Kapitalbedarf und Sicherheit"
  },
  {
    id: 6,
    title: "Seniorenfinanzierung",
    icon: MapPin,
    description: "Kredit gegen Immobilie als Sicherheit ohne monatliche Raten",
    features: ["Flexible Auszahlung", "Keine Tilgung zu Lebzeiten", "Rückzahlung bei Tod oder Übernahme durch Erben"],
    idealFor: "Temporären Liquiditätsbedarf mit Eigentumserhalt"
  }
];

const RentingOptions = () => {
  return (
    <section className="py-16 md:py-24">
      <div className="container-comfortable">
        {/* Team Image Section */}
        <div className="text-center mb-12">
          <picture>
            <source 
              srcSet={janineBorisHardiWebP} 
              type="image/webp"
              sizes="(max-width: 640px) 280px, (max-width: 768px) 320px, 320px"
            />
            <img 
              src={janineBorisHardi} 
              alt="Janine und Boris Hardi - Ihre Immobilienverrentungs-Experten"
              className="w-80 h-60 object-cover rounded-xl mx-auto mb-4 shadow-lg"
              loading="lazy"
              decoding="async"
              width="320"
              height="240"
              sizes="(max-width: 640px) 280px, (max-width: 768px) 320px, 320px"
            />
          </picture>
          <p className="text-lg text-muted-foreground font-medium">
            Ein kostenloser Service von RentePlusImmobilie - Ihre Janine und Boris Hardi
          </p>
        </div>

        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
            6 bewährte Wege der Immobilienverrentung
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-balance mb-4">
            Jede Situation ist einzigartig. Finden Sie die passende Lösung 
            für Ihre persönlichen Bedürfnisse und Ziele.
          </p>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto">
            Eigenheimverrentung ist die beste Lösung für Senioren, die Kapital aus ihrer Immobilie lösen und trotzdem wohnen bleiben möchten. Über 8.000 Kunden vertrauen bereits auf unsere kostenlose Beratung zu Leibrente, Wohnrecht, Nießbrauch, Teilverkauf, Rückmiete und Seniorenfinanzierung.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-5xl mx-auto">
          {rentingOptions.map((option) => {
            const IconComponent = option.icon;
            const routeMap: Record<string, string> = {
              leibrente: "/leibrente",
              teilverkauf: "/teilverkauf",
              "3": "/rueckmiete",
              "4": "/niessbrauch",
              "5": "/wohnrecht",
              "6": "/seniorenfinanzierung",
            };
            const href = routeMap[String(option.id)] || "/";
            return (
              <a
                key={option.id}
                href={href}
                className="group block bg-card border-2 border-border rounded-2xl p-6 md:p-7 shadow-soft hover:shadow-card hover:border-accent focus:border-accent transition-all min-h-[10rem]"
              >
                <div className="flex items-start gap-4 md:gap-5">
                  <div className="bg-accent/15 p-4 rounded-xl flex-shrink-0 group-hover:bg-accent/25 transition-colors">
                    <IconComponent className="h-9 w-9 text-accent" aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl md:text-2xl font-heading font-bold text-primary mb-2 leading-tight break-words hyphens-auto" lang="de">
                      {option.title}
                    </h3>
                    <p className="text-base md:text-lg text-foreground leading-relaxed mb-3 break-words hyphens-auto" lang="de">
                      {option.description}
                    </p>
                    <ul className="space-y-1.5 mb-4">
                      {option.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-base text-muted-foreground">
                          <CheckCircle className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" aria-hidden="true" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <span className="inline-flex items-center gap-2 text-primary font-semibold text-lg group-hover:text-accent transition-colors">
                      Mehr erfahren
                      <ArrowRight className="h-5 w-5" aria-hidden="true" />
                    </span>
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Button 
            variant="accent" 
            size="lg" 
            className="text-lg md:text-xl px-6 md:px-12 w-full max-w-md mx-auto"
            onClick={() => {
              const advisorSection = document.querySelector('[data-advisor-wizard]');
              if (advisorSection) {
                advisorSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          >
            Welche Option passt zu mir?
          </Button>
        </div>
      </div>
    </section>
  );
};

export default RentingOptions;